/**
 * Общие настройки и способ привязки проекта.
 *
 * §8.4: смена режима BOARD ↔ COLUMN — транзакционная операция, затрагивающая
 * доску, все столбцы и разрешение проекта у всех задач. Отсюда обязательное
 * подтверждение с описанием последствий, а не молчаливое переключение.
 *
 * Для смены режима серверу нужен проект доски (в BOARD) или проект каждого
 * активного столбца (в COLUMN). Прежде форма их не собирала вовсе, а обе
 * кнопки показывали «подключается вместе с backend».
 */

import { Permission, ProjectBindingMode } from "@taskflow/contracts";
import { useState } from "react";

import { usePermission, type WsError } from "@taskflow/client-core";
import {
  Button,
  FormField,
  Input,
  Modal,
  type PickerOption,
  SegmentedControl,
  Select,
  Switch,
  Textarea,
  toast,
} from "@/shared/ui";

import { useProjectsQuery } from "@taskflow/client-core/admin";
import {
  type BoardDetail,
  useChangeProjectModelMutation,
  useUpdateBoardMutation,
} from "@taskflow/client-core/boards";
import { ArchiveBoardSection } from "../BoardArchiveDialogs";
import { ViewMarkPalette } from "../ViewMarkPalette";

/** Подписи режимов — из макета. */
const MODE_LABELS = {
  [ProjectBindingMode.BOARD]: "Один проект на всю доску",
  [ProjectBindingMode.COLUMN]: "Проект для каждого столбца",
} as const;

export function GeneralSection({ board }: { board: BoardDetail }) {
  const canEditSettings = usePermission(Permission.BOARD_SETTINGS, board.permissions);
  const canChangeProject = usePermission(Permission.BOARD_PROJECT, board.permissions);
  const [updateBoard, { isLoading: saving }] = useUpdateBoardMutation();

  const [name, setName] = useState(board.name);
  const [description, setDescription] = useState(board.description ?? "");
  const [pendingMode, setPendingMode] = useState<ProjectBindingMode | null>(null);

  const unchanged = name.trim() === board.name && description === (board.description ?? "");

  const save = async () => {
    if (name.trim() === "") {
      toast.warning("Название доски не может быть пустым");
      return;
    }
    try {
      await updateBoard({
        boardId: board.id,
        version: board.version,
        name: name.trim(),
        description: description.trim() === "" ? null : description.trim(),
      }).unwrap();
      toast.success("Настройки доски сохранены");
    } catch (raw) {
      // 409: доску изменили параллельно — пользователь увидит это явно.
      toast.error(raw as WsError, "Не удалось сохранить настройки");
    }
  };

  // Переключатель сохраняется сразу, без кнопки «Сохранить»: он не связан
  // с названием и описанием и не должен ждать их правки.
  const toggleSortByStatus = async (checked: boolean) => {
    try {
      await updateBoard({
        boardId: board.id,
        version: board.version,
        sortByStatus: checked,
      }).unwrap();
      toast.success(checked ? "Сортировка по статусу включена" : "Сортировка по статусу выключена");
    } catch (raw) {
      toast.error(raw as WsError, "Не удалось изменить порядок задач");
    }
  };

  // Отметка о просмотре, как и сортировка, сохраняется сразу. Цвет здесь
  // не задаётся: он личный у каждого участника, пока не выбран — первый
  // из палитры.
  const toggleViewMark = async (checked: boolean) => {
    try {
      await updateBoard({
        boardId: board.id,
        version: board.version,
        viewMarkEnabled: checked,
      }).unwrap();
      toast.success(checked ? "Отметка о просмотре включена" : "Отметка о просмотре выключена");
    } catch (raw) {
      toast.error(raw as WsError, "Не удалось изменить отметку о просмотре");
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <section className="flex flex-col gap-3.5">
        <FormField label="Название доски" required>
          {(props) => (
            <Input
              {...props}
              value={name}
              disabled={!canEditSettings}
              onChange={(event) => setName(event.target.value)}
            />
          )}
        </FormField>

        <FormField label="Описание">
          {(props) => (
            <Textarea
              {...props}
              rows={3}
              value={description}
              disabled={!canEditSettings}
              onChange={(event) => setDescription(event.target.value)}
            />
          )}
        </FormField>

        {canEditSettings && (
          <div>
            <Button size="sm" loading={saving} disabled={unchanged} onClick={() => void save()}>
              Сохранить
            </Button>
          </div>
        )}
      </section>

      <section className="flex flex-col gap-2.5 border-t border-line pt-5">
        <h2 className="text-[13px] font-extrabold text-fg">Способ привязки проекта</h2>
        {board.isPersonal ? (
          // У личной доски проекта нет, и режим не меняется: доска
          // с проектом попала бы в отчётность.
          <p className="text-[13px] text-muted">
            Личная (без проекта): доску видите только вы, её задачи не попадают в отчётность.
          </p>
        ) : (
          <>
            <p className="text-[13px] text-muted">
              {board.projectBindingMode === ProjectBindingMode.BOARD
                ? "Сейчас все задачи доски относятся к одному проекту."
                : "Сейчас каждый столбец относится к своему проекту."}
            </p>

            <SegmentedControl<ProjectBindingMode>
              label="Способ привязки проекта"
              value={board.projectBindingMode}
              options={[
                {
                  value: ProjectBindingMode.BOARD,
                  label: MODE_LABELS[ProjectBindingMode.BOARD],
                  disabled: !canChangeProject,
                },
                {
                  value: ProjectBindingMode.COLUMN,
                  label: MODE_LABELS[ProjectBindingMode.COLUMN],
                  disabled: !canChangeProject,
                },
              ]}
              onChange={(mode) => {
                if (mode !== board.projectBindingMode) setPendingMode(mode);
              }}
            />

            {!canChangeProject && (
              <p className="text-[11px] text-faint">
                Для смены режима нужно право «Изменение режима и привязки проекта».
              </p>
            )}
          </>
        )}
      </section>

      {board.projectBindingMode === ProjectBindingMode.COLUMN && (
        <section className="flex flex-col gap-2.5 border-t border-line pt-5">
          <h2 className="text-[13px] font-extrabold text-fg">Порядок задач в столбце</h2>
          <Switch
            label="Сортировать по статусу"
            checked={board.sortByStatus}
            disabled={!canEditSettings || saving}
            onChange={(checked) => void toggleSortByStatus(checked)}
          />
          <p className="text-[13px] text-muted">
            Задачи выстраиваются по статусу: В работе, На контроле, Запланировано, Завершено.
            Внутри одного статуса сохраняется ручной порядок.
          </p>
        </section>
      )}

      {board.projectBindingMode === ProjectBindingMode.COLUMN && (
        <section className="flex flex-col gap-2.5 border-t border-line pt-5">
          <h2 className="text-[13px] font-extrabold text-fg">Отметка о просмотре</h2>
          <Switch
            label="Отметка о просмотре"
            checked={board.viewMarkEnabled}
            disabled={!canEditSettings || saving}
            onChange={(checked) => void toggleViewMark(checked)}
          />
          <p className="text-[13px] text-muted">
            В карточке задачи появится флажок «Просмотрено». Отмеченные задачи на доске
            закрашиваются выбранным цветом. Каждый участник отмечает задачи для себя —
            другие его отметок не видят. Отметки снимаются ежедневно в 00:00.
          </p>
          <p className="text-[13px] text-muted">
            Ваш цвет отметки — у других участников он свой, его можно выбрать и в карточке
            задачи.
          </p>
          <ViewMarkPalette boardId={board.id} value={board.viewMarkColor} />
        </section>
      )}

      <ArchiveBoardSection board={board} />

      {pendingMode !== null && (
        <ChangeModeDialog board={board} target={pendingMode} onClose={() => setPendingMode(null)} />
      )}
    </div>
  );
}

function ChangeModeDialog({
  board,
  target,
  onClose,
}: {
  board: BoardDetail;
  target: ProjectBindingMode;
  onClose: () => void;
}) {
  const projects = useProjectsQuery({});
  const [change, { isLoading }] = useChangeProjectModelMutation();
  const active = board.columns.filter((column) => column.isActive);

  const [projectId, setProjectId] = useState<string | null>(null);
  const [columnProjects, setColumnProjects] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const options: PickerOption[] = (projects.data?.data ?? [])
    .filter((item) => item.status === "ACTIVE")
    .map((item) => ({ id: item.id, label: item.name }));

  const toColumn = target === ProjectBindingMode.COLUMN;

  const submit = async () => {
    if (!toColumn && projectId === null) {
      setError("Выберите проект доски");
      return;
    }
    if (toColumn && active.some((column) => columnProjects[column.id] === undefined)) {
      setError("Выберите проект для каждого активного столбца");
      return;
    }
    setError(null);
    try {
      await change({
        boardId: board.id,
        version: board.version,
        projectBindingMode: target,
        ...(toColumn ? { columnProjects } : { projectId }),
      }).unwrap();
      toast.success("Способ привязки проекта изменён", MODE_LABELS[target]);
      onClose();
    } catch (raw) {
      setError((raw as WsError).data.message);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Сменить способ привязки проекта"
      width="md"
      dismissable={!isLoading}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isLoading}>
            Отмена
          </Button>
          <Button variant="danger" loading={isLoading} onClick={() => void submit()}>
            Сменить режим
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-sm text-muted">
          {toColumn
            ? "Проект доски будет снят, и каждому активному столбцу нужен свой проект. Задачи получат проект того столбца, в котором находятся."
            : "Проекты столбцов будут сняты, и все задачи доски получат единый проект доски."}
        </p>
        {/* §8.4: операция затрагивает все задачи доски — предупреждение обязательно. */}
        <p className="rounded-md bg-warning-soft px-3 py-2 text-[13px] font-semibold text-warning">
          Операция затронет все столбцы и все задачи доски. Отменить её одним действием нельзя.
        </p>

        {toColumn ? (
          active.map((column) => (
            <FormField key={column.id} label={`Проект столбца «${column.name}»`} required>
              {(props) => (
                <Select
                  {...props}
                  label={`Проект столбца «${column.name}»`}
                  placeholder="Выберите проект"
                  options={options}
                  value={columnProjects[column.id] ?? null}
                  onChange={(value) =>
                    setColumnProjects((prev) => ({ ...prev, [column.id]: value }))
                  }
                />
              )}
            </FormField>
          ))
        ) : (
          <FormField label="Проект доски" required>
            {(props) => (
              <Select
                {...props}
                label="Проект доски"
                placeholder="Выберите проект"
                options={options}
                value={projectId}
                onChange={setProjectId}
              />
            )}
          </FormField>
        )}

        {error !== null && (
          <p role="alert" className="text-[13px] font-semibold text-danger">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
